export default {
	templates: {
		productLanding: {
			name: 'Product landing',
			description:
				'A configurable landing page for showcasing the {{brandName}} platform.',
		},
		homeClassic: {
			name: 'Home – Classic',
			description:
				'A traditional one-page layout with hero, features, testimonials, FAQ, and CTA.',
		},
		homeSpotlight: {
			name: 'Home – Spotlight',
			description:
				'A spotlight layout with social proof, metrics, and a focused call to action.',
		},
		aboutStory: {
			name: 'About – Story',
			description:
				'Tell your origin story, values, and team journey with supporting metrics.',
		},
		contactHub: {
			name: 'Contact – Hub',
			description:
				'Provide multiple contact methods, quick answers, and a clear get-in-touch CTA.',
		},
	},
	pages: {
		product: {
			name: '{{brandName}} for teams',
			description:
				'Discover how {{brandName}} helps teams deliver modern learning experiences.',
		},
	},
	template1: {
		hero: {
			title: 'Effortless custom contract billing by brillance',
			subtitle:
				'Streamline your billing process with seamless automation for every custom contract, tailored by brillance.',
			primaryCta: 'Start for free',
			secondaryCta: 'Book a demo',
		},
		stats: {
			eyebrow: 'By the numbers',
			title: 'Operational excellence at global scale',
			description:
				'Trusted by finance, operations, and revenue teams to deliver reliable billing automation.',
			items: {
				uptime: {
					value: '99.99%',
					label: 'Uptime over the last 12 months',
				},
				automations: {
					value: '120+',
					label: 'Automations launched every quarter',
				},
				csat: {
					value: '4.9/5',
					label: 'Average customer satisfaction score',
				},
			},
		},
		logos: {
			title: 'Adopted by category-defining teams',
			subtitle:
				'From high-growth startups to global enterprises, {{brandName}} powers modern revenue operations.',
			items: {
				brillance: {
					name: 'Brillance',
					tagline: 'Fintech automation leader',
				},
				lumen: {
					name: 'Lumen',
					tagline: 'Product analytics suite',
				},
				velocity: {
					name: 'Velocity',
					tagline: 'Logistics intelligence',
				},
				apex: {
					name: 'Apex',
					tagline: 'Enterprise banking cloud',
				},
			},
		},
		features: {
			title: 'Everything you need to launch faster',
			subtitle:
				'Plan, collaborate, and launch faster with a platform built for modern teams.',
			items: {
				plan: {
					title: 'Plan your schedules',
					description:
						'Keep every initiative organized with collaborative planning tools.',
				},
				insights: {
					title: 'Data to insights in minutes',
					description: 'Turn raw data into dashboards that everyone can trust.',
				},
				security: {
					title: 'Enterprise-grade security',
					description:
						'Protect customer data with SSO, granular roles, and audit trails.',
				},
			},
		},
		testimonials: {
			title: 'Scale with confidence',
			subtitle:
				'Teams across industries rely on {{brandName}} to automate billing without losing control.',
			items: {
				casey: {
					quote:
						'Our finance team closed the quarter in hours, not days. Automated billing runs exactly the way we designed it.',
					author: 'Casey Morgan',
					role: 'VP Finance, Brillance',
				},
				jordan: {
					quote:
						'Implementation was painless. {{brandName}} adapts to our workflows and lets us experiment safely.',
					author: 'Jordan Lee',
					role: 'Head of Revenue Operations, Velocity',
				},
			},
		},
		faq: {
			title: 'Frequently asked questions',
			description:
				'Everything you need to know about launching automated billing with {{brandName}}.',
			items: {
				billing: {
					question: 'Can {{brandName}} handle complex contract billing rules?',
					answer:
						'Yes. Use conditional logic, proration, and custom schedules without writing code.',
				},
				implementation: {
					question: 'How long does implementation take?',
					answer:
						'Most teams launch in under three weeks with support from our solution architects.',
				},
				security: {
					question: 'How does {{brandName}} keep our data secure?',
					answer:
						'{{brandName}} includes SSO, role-based access, audit logs, and encryption at rest and in transit by default.',
				},
			},
		},
		pricing: {
			title: 'Flexible plans for every stage',
			subtitle:
				'Choose a package that matches your current scale and upgrade when you are ready.',
			plans: {
				starter: {
					name: 'Starter',
					price: '$149',
					period: 'per month',
					description: 'Launch automated billing for a single product line.',
					features: {
						automation: 'Workflow builder with core automation blocks',
						viewers: 'Up to 10 internal viewers included',
						support: 'Email support with 24-hour response time',
					},
					cta: 'Start free trial',
				},
				scale: {
					name: 'Scale',
					price: '$499',
					period: 'per month',
					description: 'Coordinate complex revenue operations across teams.',
					features: {
						automation: 'Advanced automation with branching logic',
						crm: 'Native CRM integrations and data sync',
						success: 'Dedicated success manager',
					},
					cta: 'Talk to sales',
				},
				enterprise: {
					name: 'Enterprise',
					price: 'Custom',
					period: 'annual agreement',
					description: 'Scale globally with advanced controls and support.',
					features: {
						sso: 'Single sign-on across business units',
						audit: 'Comprehensive audit trails and exports',
						success: '24/7 priority success coverage',
					},
					cta: 'Contact enterprise team',
				},
			},
		},
		cta: {
			eyebrow: 'Ready to automate?',
			title: 'Launch custom billing workflows in weeks',
			description:
				'Partner with our specialists to design and deploy automated billing across your stack.',
			primaryCta: 'Book your guided demo',
		},
	},
	homeClassic: {
		hero: {
			title: 'Build {{collectionEntityLabelPluralLower}} your team will love',
			subtitle:
				'Launch curated {{mainEntityLabelPluralLower}}, track progress, and keep every learner engaged from day one.',
			primaryCta: 'Start free trial',
			secondaryCta: 'Take a product tour',
		},
		features: {
			title: 'Create, curate, and launch in record time',
			subtitle:
				'Everything you need to deliver modern, branded learning experiences.',
			items: {
				builder: {
					title: 'Visual {{mainEntityLabelLower}} builder',
					description:
						'Plan curricula, modules, and live sessions with easy drag-and-drop tools.',
				},
				library: {
					title: 'Ready-to-use content library',
					description:
						'Mix in expert-built {{mainEntityLabelPluralLower}} from our marketplace with your own resources.',
				},
				security: {
					question: 'How quickly can we go live?',
					answer:
						'Most teams launch a branded academy within two weeks using starter templates.',
				},
				integrations: {
					question: 'Does {{brandName}} connect to our HRIS or LMS?',
					answer:
						'Yes. {{brandName}} connectors and API access sync progress, enrollments, and user records.',
				},
				support: {
					question: 'What support is included?',
					answer:
						'Every plan includes onboarding sessions plus chat support from real experts.',
				},
			},
		},
		cta: {
			eyebrow: 'See {{brandName}} in action',
			title: 'Deliver learning your team keeps coming back to',
			description:
				'Partner with our specialists to design your first {{mainEntityLabelLower}}.',
			primaryCta: 'Contact sales',
		},
	},
	homeSpotlight: {
		hero: {
			title: 'Where ambitious teams level up together',
			subtitle:
				'Deliver delightful training experiences with modern authoring, data, and automation.',
			primaryCta: 'Book a live demo',
		},
		stats: {
			eyebrow: 'Trusted at scale',
			title: 'Outcomes that speak for themselves',
			description:
				'{{brandName}} powers {{collectionEntityLabelPluralLower}} for companies of every size.',
			items: {
				learners: {
					value: '250K+',
					label: 'Active learners onboarded each month',
				},
				courses: {
					value: '12,000',
					label:
						'{{mainEntityLabelPlural}} launched with {{brandName}} templates',
				},
				rating: {
					value: '4.8/5',
					label: 'Average learner satisfaction score',
				},
			},
		},
		logos: {
			title: 'Platform of choice for category leaders',
			subtitle:
				'From fintech to healthcare, {{brandName}} keeps teams future-ready.',
			items: {
				alpha: {
					name: 'Alpha',
					tagline: 'Digital banking innovator',
				},
				nova: {
					name: 'Nova',
					tagline: 'Health tech intelligence',
				},
				pioneer: {
					name: 'Pioneer',
					tagline: 'Global logistics platform',
				},
			},
		},
		cta: {
			eyebrow: 'Ready to join them?',
			title: 'Launch your next learning initiative in weeks, not months',
			description:
				'Pick a template, customize the content, and go live with confidence.',
			primaryCta: 'View pricing',
		},
	},
	aboutStory: {
		hero: {
			title: 'We exist to help teams learn faster together',
			subtitle:
				'{{brandName}} started as a small collective of learning designers who believed knowledge should travel at startup speed.',
			primaryCta: 'Meet the team',
		},
		stats: {
			eyebrow: 'A snapshot of {{brandName}} today',
			title: 'Global reach with a people-first culture',
			description:
				'We partner with learning teams across industries and continents.',
			items: {
				years: {
					value: '8 years',
					label:
						'Helping teams build transformational {{collectionEntityLabelPluralLower}}',
				},
				team: {
					value: '180+',
					label: '{{brandName}} teammates across 12 countries',
				},
				clients: {
					value: '600+',
					label: 'Organizations building with {{brandName}} worldwide',
				},
			},
		},
		values: {
			title: 'What guides our work',
			subtitle:
				'We stay grounded in three core values as we scale {{brandName}}.',
			items: {
				innovation: {
					title: 'Invent with learners in mind',
					description:
						'We prototype relentlessly so teams can deliver delightful learning faster.',
				},
				people: {
					title: 'Trust in people-first learning',
					description:
						'We believe culture is created when people feel supported to grow.',
				},
				impact: {
					title: 'Measure what matters',
					description:
						'From outcomes to equity, we focus on learning experiences that move the needle.',
				},
			},
		},
		faq: {
			title: 'Inside {{brandName}}',
			description: 'Peek behind the scenes at how we work.',
			items: {
				location: {
					question: 'Where are you based?',
					answer:
						'Our distributed team spans North America, LATAM, Europe, and APAC, with hubs in NYC and Barcelona.',
				},
				remote: {
					question: 'Do you offer remote roles?',
					answer:
						'Yes. Most roles are remote-friendly with optional hub coworking days.',
				},
				culture: {
					question: 'What’s the culture like?',
					answer:
						'We combine high autonomy with collaborative rituals that keep us aligned and energized.',
				},
			},
		},
		cta: {
			eyebrow: 'Work with us',
			title: 'We’re building the future of learning—join us',
			description:
				'Explore open roles or tell us how {{brandName}} can support your next initiative.',
			primaryCta: 'Explore roles',
		},
	},
	contactHub: {
		hero: {
			title: 'Let’s plan your next learning milestone',
			subtitle:
				'Sales, success, and product specialists are ready to help you design the right solution.',
			primaryCta: 'Book a consultation',
		},
		methods: {
			title: 'Talk to us the way that works for you',
			subtitle:
				'Connect with the {{brandName}} team through any of these channels.',
			items: {
				phone: {
					title: 'Call us directly',
					description:
						'Reach a learning advisor Monday–Friday, 9am–6pm local time.',
				},
				email: {
					title: 'Email our team',
					description:
						'Share project details and we’ll reply within one business day.',
				},
				meeting: {
					title: 'Schedule a video session',
					description: 'Pick a time for a deep dive with a product specialist.',
				},
			},
		},
		faq: {
			title: 'Quick answers',
			description: 'A few clarifications before we connect.',
			items: {
				responseTime: {
					question: 'How fast do you respond?',
					answer:
						'Most inquiries receive a response in under 24 hours on business days.',
				},
				sales: {
					question: 'Is there a free trial?',
					answer:
						'Yes. Every plan starts with a 14-day pilot guided by our onboarding team.',
				},
				support: {
					question: 'How do I contact support?',
					answer:
						'Existing customers can log in to submit a ticket or message us via in-app chat anytime.',
				},
			},
		},
		cta: {
			eyebrow: 'We’re here to help',
			title: 'Get tailored guidance for your learning goals',
			description:
				'Tell us what you’re building and we’ll share best practices, templates, and next steps.',
			primaryCta: 'Email {{brandName}}',
		},
	},
}
